import { max, min } from 'lodash';
import { unitOfTime } from 'moment';
import { DateRange, extendMoment } from 'moment-range';
import Moment from 'moment-timezone';
import { Measurement } from './helpers/measurement';

// @ts-ignore
const moment = extendMoment(Moment);
// @ts-ignore
moment.tz.setDefault('America/New_York');

type TimeScale = 'day' | 'week' | 'month' | 'year';

const validTimescales = ['day', 'week', 'month', 'year'];

interface IDisplayInfo {
  by: unitOfTime.Diff;
  format: string;
}

interface IDisplayInfoMap {
  [key: string]: IDisplayInfo;
}

export class QualaRange {
  // takes a momentjs Range
  // interval can be day, week, month

  public displayInfo: IDisplayInfoMap = {
    week: { by: 'day', format: 'ddd' },
    month: { by: 'day', format: 'D' },
    year: { by: 'month', format: 'MMM' }
  };
  public range: DateRange;
  public timescale: TimeScale;

  constructor(range: DateRange, timescale: TimeScale) {
    this.range = range.clone();
    this.timescale = timescale;
  }

  get groupBy(): unitOfTime.Diff {
    return this.displayInfo[this.timescale].by;
  }

  get formatBy(): string {
    return this.displayInfo[this.timescale].format;
  }

  get tickFormat(): string {
    return this.formatBy;
  }

  get start(): Date {
    return this.range.start.toDate();
  }

  get end(): Date {
    return this.range.end.toDate();
  }

  private get startMoment() {
    return this.range.start;
  }

  private get endMoment() {
    return this.range.end;
  }

  get by() {
    return this.range.by;
  }

  get contains() {
    return this.range.contains;
  }

  get previous() {
    return getRange(this.previousAnchorDate, this.timescale);
  }

  get next() {
    return getRange(this.nextAnchorDate, this.timescale);
  }

  get previousAnchorDate() {
    return this.anchorDateMoment
      .clone()
      .subtract(1, this.timescale)
      .toDate();
  }

  get nextAnchorDate() {
    return this.anchorDateMoment
      .clone()
      .add(1, this.timescale)
      .toDate();
  }

  /**
   * used for date addition for finding the next and previous periods
   *
   * returns a Moment
   *
   * @return {*}
   *
   */
  public get anchorDateMoment() {
    return this.range.start;
  }

  public get anchorDate() {
    return this.anchorDateMoment.toDate();
  }

  public tick() {
    return this.range.by(this.groupBy);
  }

  /**
   * for the range it approrate formatted ticks for the range
   *
   * for a week:  Mon, Tue, Wed
   * for Month:  1,2 ... 28
   * for Year: Sept, Oct ...
   *
   * @returns {any[]}
   */
  public ticks() {
    return Array.from(this.tick()).map(t => t.toDate());
  }

  public get tickCount(): number {
    return Array.from(this.tick()).length;
  }

  public formatTick(t: Date) {
    return moment(t).format(this.tickFormat);
  }

  public firstTick() {
    return min(this.ticks());
  }

  public lastTick() {
    return max(this.ticks());
  }

  public formattedRange() {
    if (this.timescale === 'month') {
      return this.startMoment.format('MMMM Y');
    }
    if (this.timescale === 'week') {
      const startMonth = this.startMoment.format('MMM');
      const endMonth = this.endMoment.format('MMM');
      return `${this.startMoment.format('MMM D')} – ${
        startMonth === endMonth
          ? this.endMoment.format('D, YYYY')
          : this.endMoment.format('MMM D, YYYY')
      }`;
    }
    if (this.timescale === 'year') {
      return this.startMoment.format('YYYY');
    }
    throw new Error('unknown timescale');
  }

  /**
   * returns the measurments that fall into the range
   * @param args
   */
  public filter(args: { measurements: Measurement[]; getDate: any }): Measurement[] {
    const { measurements, getDate } = args;

    return measurements.reduce((acc: Measurement[], meas: Measurement) => {
      if (this.range.contains(moment(getDate(meas)))) {
        acc.push(meas);
      }
      return acc;
    }, []);
  }
}

export function getRange(date: Date, timescale: TimeScale): QualaRange {
  if (!validTimescales.includes(timescale)) {
    throw new Error("'Timescale must be either: 'day','week' 'month' or 'year");
  }

  const beginDate = moment(date)
    .startOf(timescale)
    .toDate();
  const endDate = moment(date)
    .endOf(timescale)
    .toDate();

  return new QualaRange(moment.range(beginDate, endDate), timescale);
}
